import { type ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import { type FieldEnvelope } from "../schema.js";
import { useStudio } from "./StudioContext.js";

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------

export type Lang = "en" | "fr" | "it" | "zh";

const L: Record<string, Partial<Record<Lang, string>>> = {
  s1:  { en:"STYLE HEADER",               fr:"EN-TÊTE DE STYLE",          it:"INTESTAZIONE STILE",       zh:"款式信息"     },
  s2:  { en:"BILL OF MATERIALS",          fr:"NOMENCLATURE",               it:"DISTINTA MATERIALI",       zh:"物料清单"     },
  s3:  { en:"MEASUREMENTS (CM)",          fr:"MESURES (CM)",               it:"MISURE (CM)",              zh:"尺寸 (CM)"   },
  s4:  { en:"PATTERN PIECES",             fr:"PIÈCES DU PATRON",           it:"PEZZI CARTAMODELLO",       zh:"版片"       },
  s5:  { en:"DESCRIPTION",                fr:"DESCRIPTION",                it:"DESCRIZIONE",              zh:"描述"       },
  s6:  { en:"STYLE IDENTIFICATION",       fr:"IDENTIFICATION DU STYLE",    it:"IDENTIFICAZIONE STILE",    zh:"款式识别"     },
  s7:  { en:"CONSTRUCTION INSTRUCTIONS",  fr:"INSTRUCTIONS DE CONSTRUCTION",it:"ISTRUZIONI DI COSTRUZIONE",zh:"工艺说明"     },
  s8:  { en:"CONSTRUCTION DETAILS",       fr:"DÉTAILS DE CONSTRUCTION",    it:"DETTAGLI COSTRUZIONE",     zh:"工艺细节"     },
  product_category:  { en:"Product Category",    fr:"Catégorie produit",    it:"Categoria prodotto",   zh:"产品类别"  },
  project_name:      { en:"Style Name",         fr:"Nom du modèle",        it:"Nome modello",        zh:"款名"  },
  style_number:      { en:"Style Number",          fr:"Numéro de style",      it:"Numero stile",         zh:"款号"     },
  season:            { en:"Season",                fr:"Saison",               it:"Stagione",             zh:"季节"     },
  version:           { en:"Version",               fr:"Version",              it:"Versione",             zh:"版本"     },
  size_range:        { en:"Size Range",             fr:"Gamme de tailles",     it:"Gamma taglie",         zh:"尺码范围"  },
  size_system:       { en:"Size System",            fr:"Système de tailles",   it:"Sistema taglie",       zh:"尺码系统"  },
  load_sizes:        { en:"LOAD STANDARD SIZES",   fr:"TAILLES STANDARD",     it:"TAGLIE STANDARD",      zh:"加载标准尺寸" },
  col_component:     { en:"Component",   fr:"Composant",      it:"Componente",    zh:"部件"     },
  col_material:      { en:"Material",    fr:"Matériau",       it:"Materiale",     zh:"物料"     },
  col_composition:   { en:"Composition", fr:"Composition",    it:"Composizione",  zh:"成分"     },
  col_gsm:           { en:"GSM",              fr:"GSM",            it:"GSM",           zh:"克重"     },
  col_color:         { en:"Color Code",         fr:"Couleur/Code",   it:"Colore/Codice", zh:"颜色/编码"  },
  col_fabric_code:   { en:"Material Code",          fr:"Code tissu",     it:"Codice tessuto",zh:"面料编码"  },
  col_width:         { en:"Width",        fr:"Largeur tissu",  it:"Larghezza",     zh:"幅宽"     },
  col_price:         { en:"Unit Price",        fr:"Prix",           it:"Prezzo",        zh:"价格"     },
  col_swatch:        { en:"Swatch",           fr:"Échantillon",    it:"Campione",      zh:"面料图"    },
  interlining:       { en:"INTERLINING / INTERFACING", fr:"ENTOILAGE", it:"INTERFACCIA", zh:"衬布/粘合衬" },
  add_row:           { en:"+ ADD ROW",         fr:"+ AJOUTER",       it:"+ AGGIUNGI",    zh:"+ 添加行"   },
  sample_size:       { en:"SAMPLE SIZE",       fr:"TAILLE ÉCHANTILLON", it:"TAGLIA CAMPIONE", zh:"样品尺码" },
  piece_name:        { en:"PIECE NAME",        fr:"NOM DE PIÈCE",    it:"NOME PEZZO",     zh:"版片名称"  },
  qty:               { en:"QTY",               fr:"QTÉ",             it:"QTÀ",            zh:"数量"     },
  notes:             { en:"NOTES",             fr:"NOTES",           it:"NOTE",           zh:"备注"     },
  add_piece:         { en:"+ ADD PIECE",        fr:"+ AJOUTER PIÈCE", it:"+ AGGIUNGI PEZZO",zh:"+ 添加版片" },
  si_category:       { en:"Category",      fr:"Catégorie",       it:"Categoria",      zh:"类别"     },
  si_style_type:     { en:"Style Type", fr:"Type de style",   it:"Tipo stile",     zh:"款式类型"  },
  si_silhouette:     { en:"Silhouette",    fr:"Silhouette",      it:"Silhouette",     zh:"廓形"     },
  si_length:         { en:"Length",        fr:"Longueur",        it:"Lunghezza",      zh:"长度"     },
  si_closure:        { en:"Closure",   fr:"Fermeture",       it:"Chiusura",       zh:"闭合类型"  },
  si_lining:         { en:"Lining",        fr:"Doublure",        it:"Fodera",         zh:"里料"     },
  cs_piece:          { en:"PIECE/AREA",         fr:"PIÈCE/ZONE",      it:"PEZZO/AREA",     zh:"部位/区域"  },
  cs_stitch:         { en:"STITCH TYPE",        fr:"TYPE DE POINT",   it:"TIPO PUNTO",     zh:"针迹类型"  },
  cs_seam:           { en:"SEAM FINISH",        fr:"FINITION COUTURE",it:"FINITURA CUCITURA",zh:"缝份处理" },
  cs_spi:            { en:"SPI/THREAD",         fr:"PPC/FIL",         it:"PPP/FILO",       zh:"针距/线"  },
  add_step:          { en:"+ ADD STEP",         fr:"+ AJOUTER ÉTAPE", it:"+ AGGIUNGI PASSO",zh:"+ 添加步骤" },
  cd_seam:           { en:"Seam Allowance",fr:"Marge de couture",it:"Margine cucitura",zh:"缝份"    },
  cd_tolerance:      { en:"Tolerance",     fr:"Tolérance",       it:"Tolleranza",     zh:"公差"     },
  cd_prod_type:      { en:"Production Type",    fr:"Type production",  it:"Tipo produzione",zh:"生产类型"  },
  cd_special:        { en:"Special Techniques", fr:"Techniques spéciales",it:"Tecniche speciali",zh:"特殊工艺" },
  cd_hardware:       { en:"Hardware & Trim",    fr:"Quincaillerie",   it:"Ferramenta",     zh:"辅料"     },
  cd_add_notes:      { en:"Additional Notes",   fr:"Notes supplémentaires",it:"Note aggiuntive",zh:"其他备注" },
  cd_factory:        { en:"INTERNAL — NOT SHOWN ON EXPORTS", fr:"INTERNE — NON AFFICHÉ", it:"INTERNO — NON MOSTRATO", zh:"工厂备注 — 不显示在导出中" },
  img_sketch:        { en:"Design Sketch",      fr:"Croquis de design",it:"Schizzo design",  zh:"设计草图"  },
  img_drop:          { en:"Drop image or click to upload", fr:"Déposez ou cliquez", it:"Trascina o clicca", zh:"拖放或点击上传" },
};

export function t(key: string, lang: string): string {
  const e = L[key];
  if (!e) return key;
  return e[lang as Lang] ?? e.en ?? key;
}

// ---------------------------------------------------------------------------
// HighlightCtx — AI field highlight state (yellow border, 0.8s)
// ---------------------------------------------------------------------------

export const HighlightCtx = createContext<ReadonlySet<string>>(new Set());

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="sf-section">
      <div className="sf-section-hd">
        <h2 className="sf-section-title">{title}</h2>
      </div>
      <div className="sf-section-bd">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// useYField — subscribe to one FieldEnvelope key in a Y.Map
// ---------------------------------------------------------------------------

export function useYField(
  ymap: Y.Map<FieldEnvelope>,
  key: string,
): [string, (v: string) => void] {
  const { userId, languageCode } = useStudio();
  const [val, setVal] = useState(() => {
    const e = ymap.get(key);
    return e !== undefined ? String(e.value) : "";
  });
  useEffect(() => {
    const h = () => { const e = ymap.get(key); setVal(e !== undefined ? String(e.value) : ""); };
    ymap.observe(h);
    return () => ymap.unobserve(h);
  }, [ymap, key]);
  const set = useCallback(
    (v: string) => {
      setVal(v);
      ymap.doc?.transact(() => {
        ymap.set(key, { value: v, language_code: languageCode, last_edited_by: userId, timestamp: Date.now() });
      });
    },
    [ymap, key, userId, languageCode],
  );
  return [val, set];
}

// ---------------------------------------------------------------------------
// useYStringField — subscribe to one key in a Y.Map<string> (images)
// ---------------------------------------------------------------------------

export function useYStringField(
  ymap: Y.Map<string>,
  key: string,
): [string, (v: string) => void] {
  const [val, setVal] = useState(() => ymap.get(key) ?? "");
  useEffect(() => {
    const h = () => setVal(ymap.get(key) ?? "");
    ymap.observe(h);
    return () => ymap.unobserve(h);
  }, [ymap, key]);
  const set = useCallback(
    (v: string) => {
      setVal(v);
      ymap.doc?.transact(() => ymap.set(key, v));
    },
    [ymap, key],
  );
  return [val, set];
}

// ---------------------------------------------------------------------------
// Y.Array mutation helpers
// ---------------------------------------------------------------------------

export function yPush<T>(arr: Y.Array<T>, row: T) {
  arr.doc?.transact(() => arr.push([row]));
}

export function yUpdate<T>(arr: Y.Array<T>, idx: number, row: T) {
  arr.doc?.transact(() => { arr.delete(idx, 1); arr.insert(idx, [row]); });
}

export function yDelete<T>(arr: Y.Array<T>, idx: number) {
  arr.doc?.transact(() => arr.delete(idx, 1));
}

// ---------------------------------------------------------------------------
// TextField / TextAreaField
// ---------------------------------------------------------------------------

export function TextField({
  ymap, fieldKey, label, placeholder, className,
}: {
  ymap: Y.Map<FieldEnvelope>; fieldKey: string;
  label?: string; placeholder?: string; className?: string;
}) {
  const [val, set] = useYField(ymap, fieldKey);
  const highlighted = useContext(HighlightCtx);
  return (
    <div className={className}>
      {label && <label className="sf-label">{label}</label>}
      <input type="text" value={val} onChange={(e) => set(e.target.value)}
        placeholder={placeholder} aria-label={label} className="sf-input" data-field-key={fieldKey}
        style={highlighted.has(fieldKey) ? { border: "1px solid #FFE66D" } : undefined} />
    </div>
  );
}

export function TextAreaField({
  ymap, fieldKey, label, rows = 3, note,
}: {
  ymap: Y.Map<FieldEnvelope>; fieldKey: string;
  label?: string; rows?: number; note?: string;
}) {
  const [val, set] = useYField(ymap, fieldKey);
  const highlighted = useContext(HighlightCtx);
  return (
    <div>
      {label && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <label className="sf-label" style={{ marginBottom: 0 }}>{label}</label>
          {note && <span style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{note}</span>}
        </div>
      )}
      <textarea rows={rows} value={val} onChange={(e) => set(e.target.value)}
        aria-label={label} className="sf-textarea" data-field-key={fieldKey}
        style={highlighted.has(fieldKey) ? { border: "1px solid #FFE66D" } : undefined} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// UploadBox — large image upload area
// ---------------------------------------------------------------------------

export function UploadBox({
  ymap, fieldKey, label, lang, large,
}: {
  ymap: Y.Map<string>; fieldKey: string; label: string; lang: string; large?: boolean;
}) {
  const [src, setSrc] = useYStringField(ymap, fieldKey);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const r = e.target?.result;
      if (typeof r === "string") {
        setSrc(r);
        window.dispatchEvent(new CustomEvent("studio:fileAssetUploaded", {
          detail: { slot: fieldKey, sourcePanel: "form", base64: r, fileType: file.type },
        }));
      }
    };
    reader.readAsDataURL(file);
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
      onDragOver={(e) => e.preventDefault()}
      className={`sf-upload-box${large ? " large" : " small"}`}
    >
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      {src ? (
        <img src={src} alt={label} className="sf-upload-img" />
      ) : (
        <div className="sf-upload-inner">
          <div className="sf-upload-lbl">{label}</div>
          <div className="sf-upload-hint">{t("img_drop", lang)}</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SwatchCell — compact per-row swatch upload for table cells
// ---------------------------------------------------------------------------

export function SwatchCell({ ymap, fieldKey }: { ymap: Y.Map<string>; fieldKey: string }) {
  const [src, setSrc] = useYStringField(ymap, fieldKey);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => { const r = e.target?.result; if (typeof r === "string") setSrc(r); };
    reader.readAsDataURL(file);
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
      onDragOver={(e) => e.preventDefault()}
      style={{ width: 112, height: 112, cursor: "pointer", border: "1px dashed #333", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}
    >
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      {src
        ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        : <span style={{ fontSize: 16, color: "#444", lineHeight: 1 }}>+</span>
      }
    </div>
  );
}

// ---------------------------------------------------------------------------
// Design Sketch — large image at the very top of the form
// ---------------------------------------------------------------------------

export function DesignSketchTop() {
  const { techPack, languageCode: lang } = useStudio();
  return (
    <div style={{ marginBottom: 20 }}>
      <UploadBox ymap={techPack.images} fieldKey="design_sketch" label={t("img_sketch", lang)} lang={lang} large />
    </div>
  );
}
